// Lettings → Available (/app/lettings/available, ?sub=available redirects here; docs/plans/lettings-one-form.md, "26 Oct 2026, later: Lettings without
// sheets"). User: "agents are also able to see what is available from listing, not as per the sheets". Every free flat
// with a published advert (GET /lettings/available — scoped like the listings, so an outside agent sees only their
// developments), on a sheet or not: Property · Flat · Floor · Type · Size · Rent pcm (the advert) · Available from.
// Since 3 Nov 2026 (docs/plans/lettings-ui-simplification.md, phase 1) it is the same DataTable as Viewings and Offers:
// its toolbar (search · "n of m" · Filter) and its paging, 20 rows a page. The rows are already loaded, so the filtering
// and paging stay here and DataTable gets the page and a meta built locally. No intro line, no View on site and no Refer a
// tenant button: a row click opens the flat's application form (the ReferDrawer) for anyone holding reservations.reserve;
// for everyone else the row is plain. On a phone the columns marked hideOnMobile drop out (Flat · Type · Rent · Available
// from stay) instead of the old cards. The Filter: Property · Floor · Type · Available (Available now = no date or on or
// before today, Coming free = later). The page's tiles (pages/Lettings.tsx, 3 Nov 2026 phase 2) set ?available=now | later
// while it is open and the tab follows; with it gone (the tile clicked again) Available is off. The Filter's Apply drops it.
// ?unit=<unitId> opens the ReferDrawer for that flat (&lead=<leadId> pre-fills it from a viewing request — "Apply for this
// tenant" on a completed viewing, for staff and the agent who hosted it, since 3 Nov 2026).
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../ui';
import { DataTable, type Column } from '../ui/DataTable';
import { FilterMenu } from '../ui/FilterMenu';
import { useCan } from '../../hooks/usePermission';
import { reservationsApi, floorLabel, bedsLabel, type AvailableFlat } from '../../lib/reservations';
import { formatMoney, formatDate } from '../../lib/viz';
import { londonParts } from '../../lib/dates';
import { ReferDrawer } from './ReferDrawer';

const PER_PAGE = 20;
/** The Available filter: free now (no date, or on or before today in London) or coming free later. */
const AVAILABLE = [{ value: 'now', label: 'Available now' }, { value: 'later', label: 'Coming free' }];
const typeOf = (f: AvailableFlat) => bedsLabel(f.bedrooms, f.unitType);

export function AvailableTab() {
  const isStaff = useCan('reservations.update');
  const canRefer = useCan('reservations.reserve');
  const [params, setParams] = useSearchParams();
  const q = useQuery({ queryKey: ['/lettings/available'], queryFn: () => reservationsApi.available() });
  const rows = useMemo(() => q.data ?? [], [q.data]);
  const [search, setSearch] = useState('');
  const [property, setProperty] = useState(params.get('propertyId') ?? '');
  const [floor, setFloor] = useState('');
  const [type, setType] = useState('');
  const linkedAvailable = AVAILABLE.some((a) => a.value === params.get('available')) ? params.get('available')! : '';
  const [available, setAvailable] = useState(linkedAvailable);
  const [seenAvailable, setSeenAvailable] = useState(linkedAvailable);
  if (seenAvailable !== linkedAvailable) { setSeenAvailable(linkedAvailable); setAvailable(linkedAvailable); }
  const properties = useMemo(() => [...new Map(rows.map((r) => [r.propertyId, r.propertyName])).entries()].sort((a, b) => a[1].localeCompare(b[1])), [rows]);
  const inProperty = useMemo(() => (property ? rows.filter((r) => r.propertyId === property) : rows), [rows, property]);
  const floors = [...new Set(inProperty.map((f) => f.floor))].sort((a, b) => (a ?? -1) - (b ?? -1));
  const types = [...new Set(inProperty.map(typeOf))].sort((a, b) => (a === 'Studio' ? -1 : b === 'Studio' ? 1 : a.localeCompare(b)));
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    const today = londonParts(new Date().toISOString()).day;
    return inProperty.filter((f) => {
      if (floor && String(f.floor) !== floor) return false;
      if (type && typeOf(f) !== type) return false;
      if (available) { const now = !f.availableFrom || f.availableFrom.slice(0, 10) <= today; if ((available === 'now') !== now) return false; }
      if (!term) return true;
      return [f.reference, f.name, f.propertyName, f.headline, typeOf(f)].some((v) => String(v ?? '').toLowerCase().includes(term));
    });
  }, [inProperty, search, floor, type, available]);

  // Back to page 1 whenever the search or a filter changes.
  const [page, setPage] = useState(1);
  const pageKey = JSON.stringify([search, property, floor, type, available]);
  const [seenKey, setSeenKey] = useState(pageKey);
  if (seenKey !== pageKey) { setSeenKey(pageKey); setPage(1); }
  const totalPages = Math.max(1, Math.ceil(shown.length / PER_PAGE));
  const at = Math.min(page, totalPages);
  const pageRows = shown.slice((at - 1) * PER_PAGE, at * PER_PAGE);

  const unit = params.get('unit');
  const lead = params.get('lead');
  const setUnit = (id: string | null) => { const n = new URLSearchParams(params); if (id) n.set('unit', id); else { n.delete('unit'); n.delete('lead'); } setParams(n, { replace: true }); };
  const columns = useMemo<Column<AvailableFlat>[]>(() => [
    { key: 'propertyName', label: 'Property', hideOnMobile: true, render: (f) => <span className="whitespace-nowrap text-ink-700">{f.propertyName}</span> },
    { key: 'reference', label: 'Flat', render: (f) => <span className="whitespace-nowrap font-mono font-semibold">{f.reference}</span> },
    { key: 'floor', label: 'Floor', hideOnMobile: true, render: (f) => <span className="whitespace-nowrap">{floorLabel(f.floor)}</span> },
    { key: 'type', label: 'Type', sortValue: typeOf, render: (f) => <span className="whitespace-nowrap">{typeOf(f)}</span> },
    { key: 'areaSqft', label: 'Size', hideOnMobile: true, render: (f) => <span className="whitespace-nowrap">{f.areaSqft ? `${f.areaSqft} sq ft` : '—'}</span> },
    { key: 'advertMinor', label: 'Rent pcm', align: 'right', render: (f) => formatMoney(f.advertMinor, f.currency) },
    { key: 'availableFrom', label: 'Available from', render: (f) => <span className="whitespace-nowrap">{f.availableFrom ? formatDate(f.availableFrom) : 'Now'}</span> },
  ], []);
  const filtered = !!(search.trim() || property || floor || type || available);

  return (
    <div>
      <PageHeader eyebrow={isStaff ? 'Lettings' : 'Agent portal'} title="Available flats" />
      <DataTable columns={columns} rows={pageRows} meta={{ total: shown.length, page: at, perPage: PER_PAGE, totalPages }} page={at} onPage={setPage}
        isLoading={q.isLoading} isFetching={q.isFetching} rowKey={(f) => f.unitId} onRowClick={canRefer ? (f) => setUnit(f.unitId) : undefined}
        search={search} onSearch={setSearch} searchPlaceholder="Flat, property…"
        filters={<>
          {q.data && <span className="text-[12px] tabular-nums text-ink-500">{shown.length} of {rows.length}</span>}
          <FilterMenu value={{ property: property ? [property] : [], floor: floor ? [floor] : [], type: type ? [type] : [], available: available ? [available] : [] }}
            onChange={(v) => {
              setProperty(v.property?.[0] ?? ''); setFloor(v.floor?.[0] ?? ''); setType(v.type?.[0] ?? ''); setAvailable(v.available?.[0] ?? '');
              if (params.has('available')) { const n = new URLSearchParams(params); n.delete('available'); setSeenAvailable(''); setParams(n, { replace: true }); }
            }}
            groups={[
              { key: 'property', label: 'Property', single: true, options: properties.map(([value, label]) => ({ value, label })) },
              { key: 'floor', label: 'Floor', single: true, options: floors.map((f) => ({ value: String(f), label: floorLabel(f) })) },
              { key: 'type', label: 'Type', single: true, options: types.map((t) => ({ value: t, label: t })) },
              { key: 'available', label: 'Available', single: true, options: AVAILABLE },
            ]} />
        </>}
        emptyTitle={q.error ? (q.error as Error).message : rows.length && filtered ? 'No flats match those filters' : 'No free flat has a live advert just now'}
        emptyBody={q.error ? undefined : rows.length && filtered ? 'Try another search, Property, Floor, Type or Available.' : 'A flat shows here once its listing is published.'} />
      {unit && <ReferDrawer key={unit} unitId={unit} leadId={lead} onClose={() => setUnit(null)} />}
    </div>
  );
}
